// v1.1 - CHAT-ERR-SAFE-LIST-01: adapt safe list to what the backend actually
//        sends. Exact strings for fixed reasons; shape-checked regex patterns
//        for reasons that carry a number so the match cannot be widened by
//        anything unsafe. - 2026-09-14
//
// SESSION 401 audit found three strings on the v1.0 exact list that never
// resolved against production text:
//   - "Monthly question quota exhausted" (backend sends the same phrase
//     followed by "(N,NNN questions). Resets at the start of next month.")
//   - "Rate limit exceeded" (chat rate limit is sent as
//     "Query rate limit exceeded (N QPM). Retry in 60 seconds." — different
//     opening phrase and a number suffix)
//   - "Request not permitted under employment decision policy." (only appears
//     in a docstring at services/api/d75-agent/edgs_reframe_dispatch.py:64;
//     the actual ReframeDenied catchers raise RuntimeError("EDGS deny …") or
//     ProviderBoundaryBlocked, neither surfaces this text via HTTP)
// The employment-policy entry is removed because nothing sends it. The two
// variable-suffix reasons move to SAFE_ERROR_PATTERNS below, each matched by a
// regex that pins the whole shape (prefix + number + fixed suffix). That
// prevents accidental matches on unsafe strings that merely start with the
// prefix (for example "Monthly question quota exhausted for tenant abc").
//
// The backend reason arrives under different keys on different paths (NLP
// chat: err.response.data.detail; discovery answer: err.response.data.error,
// re-keyed by the BFF's handleProxyError). Both call sites import from here.

// Show these to the customer exactly as written. Anything not on this list
// (and not matched by SAFE_ERROR_PATTERNS below) falls back to a generic
// message.
export const SAFE_ERROR_REASONS: readonly string[] = [
  'Discovery session not found',
  'Authentication required',
  'Authentication service unavailable',
];

// Shape-checked patterns for reasons whose text includes a number the backend
// controls. Each pattern anchors both ends and pins the exact structure the
// backend emits (see services/pipeline-runner/nlp/orchestrator.py:838-859 and
// services/pipeline-runner/api/chat_routes.py:118-136). A reason that starts
// with the prefix but does not match the full shape is NOT safe and falls
// back to the generic message.
export const SAFE_ERROR_PATTERNS: readonly RegExp[] = [
  // Quota: "Monthly question quota exhausted (10,000 questions). Resets at the start of next month."
  /^Monthly question quota exhausted \(\d{1,3}(?:,\d{3})* questions\)\. Resets at the start of next month\.$/,
  // Chat rate limit: "Query rate limit exceeded (60 QPM). Retry in 60 seconds."
  /^Query rate limit exceeded \(\d+ QPM\)\. Retry in \d+ seconds\.$/,
];

// True when `reason` is on the exact-string list OR matches a shape-checked
// pattern above. Anything else is unsafe by construction.
export function isSafeReason(reason: unknown): reason is string {
  if (typeof reason !== 'string') return false;
  if (SAFE_ERROR_REASONS.includes(reason)) return true;
  return SAFE_ERROR_PATTERNS.some((re) => re.test(reason));
}

// NLP chat path (useChat): the backend reason arrives at err.response.data.detail.
// Returns the reason only when it is safe; otherwise the provided fallback.
export function resolveChatError(err: any, fallback: string): string {
  const reason = err?.response?.data?.detail;
  return isSafeReason(reason) ? reason : fallback;
}

// Discovery answer path (DiscoveryChat): the BFF re-keys the reason to
// err.response.data.error. Returns the reason only when it is safe; otherwise
// the provided fallback.
export function resolveDiscoveryError(err: any, fallback: string): string {
  const reason = err?.response?.data?.error;
  return isSafeReason(reason) ? reason : fallback;
}
